// Core imports
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { t } from 'i18next';
import classNames from 'classnames';
import moment from 'moment-timezone';

// Component imports
import { Button, Col, Form, Grid, Input, Popconfirm, Row, Space, Table, Tooltip, Switch } from 'antd';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { SearchOutlined } from '@ant-design/icons';
import { PlusIcon } from '../Icons/PlusIcon';
import SideModal from '../Layout/SideModal';
import StaticVariableForm from '../Forms/StaticVariableForm';
import Permissioned from '../Permissioned';
import Highlighter from 'react-highlight-words';

// Misc imports
import { DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE, PERMISSIONS_GROUP_EDITOR } from '../../constants/main';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { useCreateStaticVariableMutation, useDeleteStaticVariableMutation, useFetchStaticVariablesQuery, useUpdateStaticVariableMutation } from '../../store/staticVariables/staticVariablesApi';
import { useModalConfirm } from '../../utils/hooks/useModalConfirm';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import is from '../../utils/is';

// Constant declarations
const { useBreakpoint } = Grid;
const CREATE_VARIABLE_MODAL_KEY = 'create-static-variable';

const StaticVariablesView = () => {
  // Core Declarations
  const { tenantId, orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [filtersForm] = Form.useForm();
  const [staticVariablesForm] = Form.useForm();

  // Hook Declarations and State dependencies
  const userPermissions = useUserPermissions(orgId, tenantId);
  const { isEditingPrimaryLanguage } = useBotLanguage();
  const screens = useBreakpoint();
  const [searchParams] = useSearchParams();
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const { showModalConfirm } = useModalConfirm(hasPendingChanges);

  // State Declarations - Fixed to use proper defaults and parsing
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page !== null ? parseInt(page, 10) : 0;
  });

  const [pageSize, setPageSize] = useState(() => {
    const size = searchParams.get('pageSize');
    return size !== null ? parseInt(size, 10) : DEFAULT_PAGINATION_SIZE;
  });

  const [selectedStaticVariable, setSelectedStaticVariable] = useState(null);

  // API Declarations
  const { data: staticVariables, isFetching: isFetchingStaticVariables } = useFetchStaticVariablesQuery({
    orgId,
    tenantId,
    urlParams: {
      page: 0,
      pageSize: 9999
    }
  }, { skip: !orgId || !tenantId });
  const [createStaticVariable, { isLoading: isCreatingStaticVariable }] = useCreateStaticVariableMutation();
  const [updateStaticVariable, { isLoading: isUpdatingStaticVariable }] = useUpdateStaticVariableMutation();
  const [deleteStaticVariable, { isLoading: isDeletingStaticVariable }] = useDeleteStaticVariableMutation();
  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });

  // Misc Declarations
  const botTimezone = bot?.timezone ?? 'UTC';
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;
  let filterTimeout = 0;

  // useEffect Declarations
  useEffect(() => {
    if (tenantId) {
      // Ensure we're using the correct sortingInfo when initializing
      const sortByParam = searchParams.get('sortBy') || 'name';
      const orderParam = searchParams.get('order') || 'desc';
      updateUrlSearchParams(currentPage + 1, pageSize, { [sortByParam]: orderParam });
    }
  }, [tenantId]);

  // Monitor URL search params for changes and update state accordingly
  useEffect(() => {
    const pageParam = searchParams.get('page');
    const pageSizeParam = searchParams.get('pageSize');

    // Only update state if the URL params differ from current state
    if (pageParam !== null && parseInt(pageParam, 10) !== currentPage) {
      setCurrentPage(parseInt(pageParam, 10));
    }

    if (pageSizeParam !== null && parseInt(pageSizeParam, 10) !== pageSize) {
      setPageSize(parseInt(pageSizeParam, 10));
    }

    // Handle edit parameter
    const staticVariableToEdit = searchParams.get('edit');
    if (!staticVariableToEdit) {
      setSelectedStaticVariable(null);
    } else if (staticVariableToEdit !== selectedStaticVariable) {
      setSelectedStaticVariable(staticVariableToEdit);
    }
  }, [searchParams, staticVariables]);

  // Update URL when selectedStaticVariable changes
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);

    if (selectedStaticVariable) {
      urlSearchParams.set('edit', selectedStaticVariable);
    } else {
      urlSearchParams.delete('edit');
    }

    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    });
  }, [selectedStaticVariable]);

  // Filters, Table and Params Functions
  const formatDate = (date) => {
    return date ? moment(date).tz(botTimezone).format(DEFAULT_DATETIME_FORMAT) : '';
  };

  const filteredDataSource = useMemo(() => {
    if (!staticVariables || !is.array(staticVariables.items)) {
      return [];
    }

    const searchValue = searchParams.get('search')?.toLowerCase() || '';

    if (!searchValue) {
      return staticVariables.items;
    }

    return staticVariables.items.filter(item => {
      const name = item.name || '';
      const value = item.value || '';
      const description = item.description || '';

      return (
        name.toLowerCase().includes(searchValue) || value.toLowerCase().includes(searchValue)|| description.toLowerCase().includes(searchValue)
      );
    });
  }, [staticVariables, searchParams]);

  const sortingInfo = useMemo(() => {
    const order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'descend';
    const columnKey = searchParams.get('sortBy') || 'name';

    return {
      order,
      columnKey
    };
  }, [searchParams]);

  const updateUrlSearchParams = (current, newPageSize, sorting = { name: 'desc' }) => {
    const urlSearchParams = new URLSearchParams(location.search);
    const searchFilter = filtersForm.getFieldValue('search');

    const pageToUse = current !== undefined ? current - 1 : currentPage;
    urlSearchParams.set('page', pageToUse.toString());

    const pageSizeToUse = newPageSize || pageSize;
    urlSearchParams.set('pageSize', pageSizeToUse.toString());

    // Handle search
    if (searchFilter) {
      urlSearchParams.set('search', searchFilter);
    } else {
      urlSearchParams.delete('search');
    }

    // Handle sorting
    if (sorting) {
      const sortByKey = Object.keys(sorting)[0];
      const sortOrder = sorting[sortByKey];

      if (sortByKey && sortOrder) {
        urlSearchParams.set('sortBy', sortByKey);
        urlSearchParams.set('order', sortOrder);
      } else {
        urlSearchParams.delete('sortBy');
        urlSearchParams.delete('order');
      }
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const handleTableChange = (pagination, _filters, sorter) => {
    let sorting;
    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'asc' : 'desc' };
    } else {
      if (sorter.field === 'name' && searchParams.get('order') === 'desc') {
        sorting = { [sorter.columnKey]: 'asc' };
      }
    }
    updateUrlSearchParams(pagination.current, pagination.pageSize, sorting);
  };

  const columns = [
    {
      title: t('Name'),
      key: 'name',
      dataIndex: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      sortOrder: sortingInfo.columnKey === 'name' && sortingInfo.order,
      ellipsis: { showTitle: false },
      render: (text, record) => (
        <Tooltip overlayClassName="pointer-events-none" placement="topRight" title={record.name || ''}>
          <Highlighter
            highlightClassName="bg-highlight p-0"
            searchWords={[searchParams?.get('search')]}
            autoEscape
            className="text-overflow-ellipsis overflow-hidden white-space-nowrap"
            textToHighlight={record.name}
          />
        </Tooltip>
      )
    },
    {
      title: t('Value'),
      key: 'value',
      dataIndex: 'value',
      ellipsis: { showTitle: false },
      render: (text, record) => (
        <Tooltip overlayClassName="pointer-events-none" placement="topRight" title={record.value || ''}>
          <Highlighter
            highlightClassName="bg-highlight p-0"
            searchWords={[searchParams?.get('search')]}
            autoEscape
            className="text-overflow-ellipsis overflow-hidden white-space-nowrap"
            textToHighlight={record.value}
          />
        </Tooltip>
      )
    },
    {
      title: t('Description'),
      key: 'description',
      dataIndex: 'description',
      ellipsis: { showTitle: false },
      render: (text, record) => (
        <Tooltip overlayClassName="pointer-events-none" placement="topRight" title={record.description || ''}>
          <Highlighter
            highlightClassName="bg-highlight p-0"
            searchWords={[searchParams?.get('search')]}
            autoEscape
            className="text-overflow-ellipsis overflow-hidden white-space-nowrap"
            textToHighlight={record.description}
          />
        </Tooltip>
      )
    },
    {
      title: t('Last Updated'),
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      sorter: (a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateA - dateB;
      },
      sortOrder: sortingInfo.columnKey === 'updatedAt' && sortingInfo.order,
      ellipsis: { showTitle: false },
      render: (text, record) => (
        <Tooltip overlayClassName="pointer-events-none" placement="topRight" title={formatDate(record.updatedAt)}>
          <span className="text-overflow-ellipsis overflow-hidden white-space-nowrap">
            {formatDate(record.updatedAt)}
          </span>
        </Tooltip>
      )
    },
    {
      title: t('Constant'),
      key: 'isConstant',
      dataIndex: 'isConstant',
      align: 'center',
      width: 120,
      render: (value, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={value}
            disabled={!isEditingPrimaryLanguage}
            onClick={async (e) => {
              try {
                await updateStaticVariable({
                  orgId,
                  tenantId,
                  data: {
                    name: record.name,
                    isConstant: !value
                  }
                }).unwrap();
              } catch (error) {}
            }}
          />
        </Space>
      )
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: 160,
      render: (record) => (
        <Space onClick={(e) => e.stopPropagation()} size={4}>
          {
            userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission)) ? (
              <Fragment>
                <Tooltip overlayClassName="pointer-events-none" title={t('Edit')}>
                  <Button
                    shape="circle"
                    type="text"
                    icon={<EditIcon />}
                    onClick={() => setSelectedStaticVariable(record.name)}
                    disabled={!isEditingPrimaryLanguage || isUpdatingStaticVariable}
                  />
                </Tooltip>

                <Popconfirm
                  title={t('Delete this variable?')}
                  okText={t('Yes')}
                  cancelText={t('No')}
                  placement="topRight"
                  onConfirm={() => handleDeleteVariable(record.name)}
                  zIndex={1100}
                  disabled={!isEditingPrimaryLanguage}
                >
                  <Tooltip
                    overlayClassName="pointer-events-none"
                    title={t('Delete')}
                  >
                    <Button
                      shape="circle"
                      type="text"
                      icon={<DeleteIcon />}
                      disabled={!isEditingPrimaryLanguage}
                      loading={isDeletingStaticVariable}
                    />
                  </Tooltip>
                </Popconfirm>
              </Fragment>
            ) : null
          }
        </Space>
      )
    }
  ];

  // Form Handlers
  const onModalClose = () => {
    setSelectedStaticVariable(null);
  };

  const handleShowConfirm = (action, actionType) => {
    showModalConfirm({
      defaultAction: () => {
        action();
        setHasPendingChanges(false);
      },
      onConfirm: async () => {
        if (action) {
          const isValid = await staticVariablesForm.validateFields();
          if (!isValid) {
            return;
          } else {
            await onFormSubmit(staticVariablesForm.getFieldsValue());
            action();
          }
        }
        setHasPendingChanges(false);
      },
      onMaskClick: () => {
        return;
      },
      okText: t('Save Changes'),
      cancelText: actionType === 'select' ? t('Continue without saving') : t('Quit without saving'),
      title: t('Save Changes?'),
      content:
        actionType === 'select'
          ? t('Your unsaved changes will be lost. Save changes before continue?')
          : t('Your unsaved changes will be lost. Save changes before closing?')
    });
  };

  const onFormSubmit = async (valuesToSubmit) => {
    try {
      if (selectedStaticVariable === CREATE_VARIABLE_MODAL_KEY) {
        const response = await createStaticVariable({ orgId, tenantId, data: valuesToSubmit }).unwrap();
        if (response) {
          setSelectedStaticVariable(null);
          setHasPendingChanges(false);
        }
      } else if (selectedStaticVariable && selectedStaticVariable !== CREATE_VARIABLE_MODAL_KEY) {
        const response = await updateStaticVariable({
          orgId,
          tenantId,
          data: valuesToSubmit
        }).unwrap();

        if (response) {
          setSelectedStaticVariable(null);
          setHasPendingChanges(false);
        }
      }
    } catch (error) {}
  };

  const handleDeleteVariable = async (variableName) => {
    try {
      await deleteStaticVariable({
        orgId,
        tenantId,
        data: { name: variableName }
      }).unwrap();
    } catch (error) {}
  };

  return (
    <Fragment>
      <Row gutter={24}>
        <Col xs={24}>
          <Space className="justify-content-between w-100" wrap align="start">
            <Form
              layout={isMobileOrTabletSize ? 'vertical' : 'inline'}
              name="filtersForm"
              initialValues={{
                search: searchParams.get('search') ? decodeURIComponent(searchParams.get('search')) : ''
              }}
              form={filtersForm}
            >
              <Form.Item
                name="search"
                style={{ maxWidth: screens.xs ? '100%' : 220 }}
                className="w-100 mb-2"
              >
                <Input
                  size="large"
                  placeholder={t('Search in contents')}
                  allowClear
                  prefix={<SearchOutlined className="text-muted" />}
                  onChange={(e) => {
                    clearTimeout(filterTimeout);
                    filterTimeout = setTimeout(() => {
                      filtersForm.setFieldsValue({ search: e.target.value });
                      updateUrlSearchParams();
                    }, 400);
                  }}
                />
              </Form.Item>
            </Form>

            <Permissioned
              allowedPermissions={PERMISSIONS_GROUP_EDITOR}
              currentPermissions={userPermissions}
              fallbackComponent={null}
            >
              <Button
                type="primary"
                size="large"
                block={isMobileOrTabletSize}
                icon={<PlusIcon />}
                disabled={!isEditingPrimaryLanguage || isCreatingStaticVariable || isUpdatingStaticVariable}
                onClick={() => setSelectedStaticVariable(CREATE_VARIABLE_MODAL_KEY)}
              >
                {t('Add Variable')}
              </Button>
            </Permissioned>
          </Space>
        </Col>
      </Row>

      <Row>
        <Col xs={24}>
          <Table
            size="small"
            rowKey="name"
            columns={columns}
            rowClassName={classNames({
              'cursor-pointer': isEditingPrimaryLanguage && userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))
            })}
            onRow={(record) => ({
              onClick: () => {
                if(isEditingPrimaryLanguage && userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))){
                  setSelectedStaticVariable(record.name);
                }
              }
            })}
            onChange={handleTableChange}
            showSorterTooltip={{ visible: false }}
            {
              ...(pageSize > DEFAULT_PAGINATION_SIZE && {
                scroll: { y: 500 },
                className: 'scrollbar-custom scrollbar-custom-no-hide'
              })
            }
            pagination={{
              size: 'default',
              total: filteredDataSource?.length || 0,
              current: currentPage + 1,
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Variables')}: ${total}`
            }}
            dataSource={filteredDataSource}
            loading={isFetchingStaticVariables}
          />
        </Col>
      </Row>

      <SideModal
        title={selectedStaticVariable === CREATE_VARIABLE_MODAL_KEY ? t('Create Variable') : t('Edit Variable')}
        open={!!selectedStaticVariable}
        onClose={() => handleShowConfirm(onModalClose)}
        destroyOnClose
        bodyClassName="p-0"
      >
        <StaticVariableForm
          form={staticVariablesForm}
          isUpdateMode={!!selectedStaticVariable && selectedStaticVariable !== CREATE_VARIABLE_MODAL_KEY}
          isLoading={isUpdatingStaticVariable}
          footerClassName="ant-drawer-footer"
          bodyClassName="px-6 pt-6 flex-grow-1"
          onSubmitSuccess={onFormSubmit}
          onCancel={() => handleShowConfirm(onModalClose)}
          setHasPendingChanges={() => setHasPendingChanges(true)}
        />
      </SideModal>
    </Fragment>
  );
};

export default StaticVariablesView;
